import React, { useEffect, useState } from 'react';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import HistoryIcon from '@mui/icons-material/History';
import { CircularProgress, Container, IconButton, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableRow, Tooltip } from '@mui/material';
import _ from 'lodash';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useRecoilState } from 'recoil';
import { dspState } from '../../atoms/DspState';
import { prefixesFilterState } from '../../atoms/PrefixesFilterState';
import { prefixesManagement } from '../../atoms/PrefixesManagementState';
import { warehouseState } from '../../atoms/WarehouseState';
import Delete from '../../components/Dialog/Delete';
import DialogHistory from '../../components/DialogHistory';
import ListHeader from '../../components/ListHeader';
import { deletePrefix, getHistoryEventById, listDsp, listWarehouse, listWarehouseRegion, searchPrefix } from '../../repositories/HttpRepository';
import Filter from './filter';
import SearchForm from './searchForm';
import useStyles from './styles';
import Pagination from 'components/Pagination';

const fields = [
  {
    id: 'id',
    numeric: false,
    disablePadding: true,
    label: 'Prefix ID',
    isDisableSort: true,
    hidden: true,
  },
  {
    id: 'prefix',
    numeric: false,
    disablePadding: false,
    label: 'Prefix',
    isDisableSort: false,
  },
  {
    id: 'region_code',
    numeric: false,
    disablePadding: false,
    label: 'Region Code',
    isDisableSort: true,
  },
  {
    id: 'route_type',
    numeric: false,
    disablePadding: false,
    label: 'Route Type',
    isDisableSort: true,
  },
  {
    id: 'warehouse_id',
    numeric: false,
    disablePadding: false,
    label: 'Warehouse',
    isDisableSort: true,
  },
  {
    id: 'dsp_id',
    numeric: false,
    disablePadding: false,
    label: 'DSP ID',
    isDisableSort: true,
  },
  {
    id: 'capacity',
    numeric: false,
    disablePadding: false,
    label: 'Capacity',
    isDisableSort: true,
  },
  {
    id: 'max_box',
    numeric: false,
    disablePadding: false,
    label: 'Max Boxes',
    isDisableSort: true,
  },
  {
    id: 'zone_id',
    numeric: false,
    disablePadding: false,
    label: 'Booking Zone',
    isDisableSort: true,
  },
  {
    id: 'sprinkle',
    numeric: false,
    disablePadding: false,
    label: 'Sprinkle',
    isDisableSort: true,
  },
  {
    id: 'is_hub',
    numeric: false,
    disablePadding: false,
    label: 'Is Hub',
    isDisableSort: true,
  },
  {
    id: 'action',
    numeric: false,
    disablePadding: false,
    label: 'Action',
    isDisableSort: true,
  },
];

const PrefixesManagement = () => {
  const [showDelete, setShowDelete] = useState(false);
  const [currentSelect, setCurrentSelect] = useState();
  const [isDeleting, setIsDeleting] = useState(false);
  const [regionsList, setRegionsList] = useState([]);
  const [openHistory, setOpenHistory] = useState(false);
  const [prefix, setPrefix] = useState('');
  const [historyId, setHistoryId] = useState(null);

  const [prefixes, setPrefixes] = useRecoilState(prefixesManagement);
  const [filterStates, setFilterState] = useRecoilState(prefixesFilterState);
  const [warehouses, setWarehouses] = useRecoilState(warehouseState);
  const [dspList, setDspList] = useRecoilState(dspState);

  const { data, page, size, desc, total, isListing, search_text } = prefixes;
  const { is_desc, order_by } = filterStates.sort;

  const navigate = useNavigate();
  const location = useLocation();
  const { classes } = useStyles();

  const handleRequestSort = (event, property) => {
    const isDesc = order_by === property && is_desc;
    setFilterState({
      ...filterStates,
      selected: {
        ...filterStates.selected,
        page: 0,
      },
      sort: {
        ...filterStates.sort,
        is_desc: !isDesc,
        order_by: property,
      },
    });
  };

  const viewDetail = (id) => {
    navigate('/prefixes-management/' + id, { state: { is_desc } });
  };

  function processFilterData() {
    const params = new URLSearchParams();
    const urlParams = new URLSearchParams(location.search);
    const queryParams = Object.fromEntries(urlParams);
    const mergeParams = {
      ...queryParams,
      ...filterStates.selected,
      ...filterStates.sort,
    };

    for (const [key, value] of Object.entries(mergeParams)) {
      if (!value) {
        continue;
      }

      if (Array.isArray(value)) {
        value.forEach((tmp) => {
          params.append(key, tmp);
        });
      } else {
        params.append(key, value);
      }
    }

    const desc = _.isNil(filterStates.sort.is_desc) ? urlParams.get('is_desc') || urlParams.get('desc') || false : filterStates.sort.is_desc;
    params.set('order_by', filterStates.sort.order_by || urlParams.get('order_by') || 'prefix');
    params.set('desc', desc);
    params.delete('is_desc');

    const cloneParams = new URLSearchParams(params);
    const isNilPage = _.isNil(filterStates.selected.page);
    const currentPage = urlParams.get('page') && isNilPage ? urlParams.get('page') : isNilPage ? 1 : +filterStates.selected.page + 1;
    cloneParams.set('page', currentPage);
    navigate({ search: `?${cloneParams.toString()}` }, { replace: true });
    params.set('page', currentPage - 1);
    return params;
  }

  const handleSearch = (keyword) => {
    setFilterState({
      ...filterStates,
      selected: {
        ...filterStates.selected,
        prefix: keyword,
        page: 0,
      },
    });
  };

  const handeSearchReset = (e) => {
    setFilterState({
      ...filterStates,
      selected: {
        ...filterStates.selected,
        prefix: null,
        page: 0,
      },
    });
  };

  const onDeletePrefixHandle = () => {
    setIsDeleting(true);
    if (currentSelect?.id) {
      deletePrefix(currentSelect.id)
        .then((resp) => {
          if (resp.ok) {
            toast.success('Delete prefix successfully');
            setPrefixes({
              ...prefixes,
              data: prefixes.data.filter((item) => item.id !== currentSelect.id),
            });
            setCurrentSelect(null);
          }
        })
        .catch((err) => toast.error(err))
        .finally(() => {
          setIsDeleting(false);
          setShowDelete(false);
        });
    }
  };

  const loadPrefixList = () => {
    if (isListing) return;

    setPrefixes({ ...prefixes, isListing: true });
    searchPrefix(processFilterData())
      .then((resp) => {
        setPrefixes({ ...prefixes, isListing: false });
        if (resp.ok) {
          setPrefixes({ ...resp.data, data: resp.data.data });
        }
      })
      .catch((err) => toast.error(err));
  };

  useEffect(() => {
    if (order_by && data) {
      // const newPrefixesData = [...data].sort((a, b) =>
      //   is_desc ? a[order_by] < b[order_by] : a[order_by] > b[order_by],
      // );

      setPrefixes({
        ...prefixes,
        data: _.orderBy(data, ['prefix'], [is_desc ? 'desc' : 'asc']),
      });
    }
  }, [is_desc, order_by]);

  useEffect(() => {
    loadPrefixList();
  }, [filterStates.selected, filterStates.sort, filterStates.canRefresh]);

  useEffect(() => {
    const defaultParams = { order_by: 'id', desc: false, size: 999 };
    listWarehouse(defaultParams)
      .then((resp) => {
        if (resp.ok) {
          setWarehouses(resp.data);
        }
      })
      .catch((err) => toast.error(err));

    listDsp(defaultParams)
      .then((resp) => {
        if (resp.ok) {
          setDspList(resp.data);
        }
      })
      .catch((err) => toast.error(err));

    listWarehouseRegion()
      .then((resp) => {
        if (resp.ok) {
          setRegionsList(_.sortBy(resp.data.items));
        }
      })
      .catch((err) => toast.error(err));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setFilterState({
      ...filterStates,
      selected: {
        ...filterStates.selected,
        prefix: params.get('prefix'),
        'warehouse_ids[]': params.get('warehouse_ids[]'),
        'regions[]': params.get('regions[]'),
        page: params.get('page') ? +params.get('page') - 1 : 0,
      },
      sort: {
        ...filterStates.sort,
        order_by: params.get('order_by'),
        is_desc: params.get('desc') === 'true',
      },
    });
  }, [location.search]);

  const handleShowHistory = (id, prefix) => {
    setPrefix(prefix);
    setOpenHistory(true);
    setHistoryId(id);
  };

  const handleCloseHistory = (e) => {
    setOpenHistory(false);
    setHistoryId(null);
  };

  const handleChangePage = (event, newPage) => {
    setFilterState({
      ...filterStates,
      selected: {
        ...filterStates.selected,
        page: parseInt(newPage, 10) - 1,
      },
    });
  };

  if (warehouses.items.length === 1 || !dspList)
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </div>
    );

  const params = new URLSearchParams(location.search);
  const keyword = params.get('prefix');
  const selectedRegions = params.get('regions[]');
  const selectedWarehouseIds = params.get('warehouse_ids[]');

  return (
    <Container maxWidth={false} className={classes.root}>
      <Filter prefixes={prefixes.data} regionsList={regionsList} selectedRegions={selectedRegions} selectedWarehouseIds={selectedWarehouseIds} />
      <SearchForm defaultKeyword={keyword} onSubmit={handleSearch} reset={handeSearchReset} placeholder="Search for prefix" isDesc={is_desc} />
      <Paper className={classes.paper}>
        <TableContainer>
          <Table className={classes.table} aria-labelledby="tableTitle" aria-label="enhanced table">
            <ListHeader fields={fields} desc={is_desc} orderBy={order_by} onRequestOrder={handleRequestSort} rowCount={5} />
            <TableBody>
              {data?.map((item, index) => {
                const { id, prefix, region_code, route_type, warehouse_id, dsp_id, capacity, sprinkle, is_hub, max_box, zone_id } = item;

                const currentWarehouse = warehouses?.items?.find((warehouse) => warehouse.id === warehouse_id);
                const currentDsp = dspList?.items?.find((dps) => dps.id === dsp_id);

                return (
                  <TableRow hover tabIndex={-1} key={id} style={{ cursor: 'pointer' }}>
                    {/* <TableCell component="th" id={labelId} scope="row" padding="none">
                        {id}
                      </TableCell> */}
                    <TableCell onClick={() => viewDetail(id)}>{prefix}</TableCell>
                    <TableCell onClick={() => viewDetail(id)}>{region_code}</TableCell>
                    <TableCell onClick={() => viewDetail(id)}>{route_type}</TableCell>
                    <TableCell onClick={() => viewDetail(id)}>{currentWarehouse ? `[${warehouse_id}] ${currentWarehouse.alias ?? '-'}` : '-'}</TableCell>
                    <TableCell onClick={() => viewDetail(id)}>{currentDsp ? `[${dsp_id}] ${currentDsp.code || currentDsp.company}` : ''}</TableCell>
                    <TableCell onClick={() => viewDetail(id)}>{capacity ?? '-'}</TableCell>
                    <TableCell onClick={() => viewDetail(id)}>{max_box ?? '-'}</TableCell>
                    <TableCell onClick={() => viewDetail(id)}>{zone_id?.length > 0 ? zone_id.toUpperCase() : '-'}</TableCell>
                    <TableCell onClick={() => viewDetail(id)}>{sprinkle ? <CheckCircleIcon htmlColor="green" /> : <CancelIcon htmlColor="red" />}</TableCell>
                    <TableCell onClick={() => viewDetail(id)}>{is_hub ? <CheckCircleIcon htmlColor="green" /> : <CancelIcon htmlColor="red" />}</TableCell>
                    <TableCell style={{ display: 'flex', alignItems: 'center' }}>
                      <Tooltip title="Delete" aria-label="delete">
                        <DeleteForeverIcon
                          onClick={() => {
                            setCurrentSelect(item);
                            setShowDelete(true);
                          }}
                          className={classes.deleteIcon}
                        />
                      </Tooltip>

                      <Tooltip title="View prefix history" aria-label="view prefix history">
                        <IconButton tooltip="View history" onClick={() => handleShowHistory(id, prefix)} size="large">
                          <HistoryIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {isListing && <LinearProgress />}

          <Delete
            isOpen={showDelete}
            isLoading={isDeleting}
            labelCancel="NO"
            labelApprove="Yes, Delete Prefix"
            content={`Are you sure to delete prefix ${currentSelect?.prefix} at region ${currentSelect?.region_code}?`}
            handleCancel={() => setShowDelete(false)}
            handleApprove={onDeletePrefixHandle}
          />
        </TableContainer>
        <Pagination total={total} size={size} onChange={handleChangePage} forceUpdatePage={page ?? 0} />
      </Paper>

      <DialogHistory getEvents={getHistoryEventById} historyId={historyId} open={openHistory} handleClose={handleCloseHistory} title={`View prefix ${prefix} history`} />
    </Container>
  );
};

export default React.memo(PrefixesManagement);
